/**
 * storeLocationOfferResolver — pure, deterministic helpers that resolve a
 * `ShoppingListItem`'s `StoreLocationOffer`s, including the collapsed
 * `storeLocationOfferSummary()` deriver (the additive price layer).
 *
 * Load-bearing framing: Planning is a planning list FIRST. Every function here
 * is written so the list is fully usable with ZERO offer data — the price layer
 * only enriches. The collapsed summary is additive: `storeLocationOfferSummary`
 * returns `null` (render NOTHING) while offers load, a quiet `availabilityDot`
 * when stores have resolved but carry no offer, and a single `summaryLabel`
 * (`On sale · N stores`, else `$X best`) when ≥1 offer matches. We never
 * fabricate a price, and there is no cut-off text — the old "Not at your…"
 * string is replaced by the wordless dot.
 *
 * Matching offers to an item is best-effort by product/brand name, because the
 * client `ShoppingListItemWithDetails` and `RetailerOfferWithDetails` shapes
 * carry no shared product id to join on. See TD#1 in the task's Implementation
 * Notes — when offers carry a resolved SystemProduct id end-to-end, this should
 * become an id match.
 */

import type {
  RetailerOfferWithDetails,
  ShoppingListItemWithDetails,
} from '@shopista/shared/types';
import {
  deriveBadges,
  EMPTY_BADGE_INPUTS,
  type BadgeInputs,
  type DealBadge,
} from '../deals/badges';

// One selected store's offers, resolved (or still loading). Keyed by store id so
// the row can aggregate across every selected store before deriving a summary.
export interface StoreLocationOffers {
  storeId: string;
  storeName: string | null;
  offers: RetailerOfferWithDetails[];
  loading: boolean;
}

// A single offer matched to the planning item, tagged with where it came from
// and how it relates to the household's brand preferences. `qualifier` drives
// the row's "· your brand" / "· your pick" / "· substitute" line:
//   - `preferred` (· your brand)   — the offer's brand is a real household
//     liked brand (the strong signal, T12.5.1);
//   - `pick` (· your pick)         — the offer's brand is the brand of the offer
//     the household selected on its list (a weaker, choice-derived signal);
//   - `substitute` (· substitute)  — a different brand than either of the above;
//   - `null`                       — no brand signal at all.
export interface MatchedStoreLocationOffer {
  storeId: string;
  storeName: string | null;
  offer: RetailerOfferWithDetails;
  qualifier: 'preferred' | 'pick' | 'substitute' | null;
  // T12.5.1 (E) — the full stacked badge set for this offer (same set + order as
  // Deals). The offer is for a product ON the list, so `on-list` is always
  // present; the preference badges come from the household's signals.
  badges: DealBadge[];
}

/**
 * The collapsed `storeLocationOfferSummary` — at most ONE signal:
 *   - `{ kind: 'label' }` — a single `summaryLabel` (`On sale · N stores`, else
 *     `$X best`); shown when ≥1 offer matches.
 *   - `{ kind: 'dot' }`   — the wordless `availabilityDot`; shown when stores
 *     have resolved but none carry the item (replaces the old cut-off text).
 *   - `null`              — nothing; while offers are still loading, or no stores
 *     are connected yet.
 */
export type StoreLocationOfferSummary =
  | { kind: 'label'; label: string }
  | { kind: 'dot' };

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Does this offer carry the item's product?
 *
 * T12.5.1 (E) — prefer a SystemProduct IDENTITY match: when the item's product is
 * linked to SystemProduct id(s) and the offer carries a resolved `systemproduct_id`
 * (now surfaced on the wire, T12.5.1 B), an exact id hit is authoritative — the
 * same id rule the Deals surface uses, so the two agree. `itemSystemProductIds`
 * is the item's HouseholdProduct's linked SystemProduct id(s), threaded in.
 *
 * Falls back to the best-effort NAME match (TD#1) only when no id match is
 * available — an unresolved offer (no `systemproduct_id`) or an item with no
 * linked SystemProduct id yet. No fabrication — a miss yields no match and the
 * row reads as a plain planning item.
 */
export function offerMatchesItem(
  item: ShoppingListItemWithDetails,
  offer: RetailerOfferWithDetails,
  itemSystemProductIds: ReadonlySet<string> = new Set(),
): boolean {
  // Identity match (authoritative) — the offer's resolved SystemProduct id is one
  // of the item's product's linked SystemProduct ids.
  if (
    offer.systemproduct_id != null &&
    itemSystemProductIds.has(offer.systemproduct_id)
  ) {
    return true;
  }
  // Name fallback (TD#1) — only when no id is available to decide on.
  const needle = norm(item.product_name ?? item.display_title);
  if (!needle) return false;
  const haystacks = [
    norm(offer.system_product_name),
    norm(offer.title),
  ].filter(Boolean);
  return haystacks.some(
    (hay) => hay === needle || hay.includes(needle) || needle.includes(hay),
  );
}

/**
 * Classify how this offer's brand relates to the household's brand preferences.
 *
 * `likedBrands` is the set of the household's real liked brand NAMES, already
 * normalized (lowercased + trimmed). It is threaded in as a parameter so this
 * helper stays pure — the single household-wide brand-preferences query is read
 * ONCE at the list level (TD#2: no per-row preference fan-out), then the same
 * set is passed to every row.
 *
 * Precedence (T12.5.1):
 *   1. `preferred` — the offer's brand is a real household liked brand (the
 *      strong, durable signal). Exact normalized match, never substring.
 *   2. `pick`      — the offer's brand equals the brand of the offer the
 *      household selected on its list (`item.offer_brand`); a weaker signal
 *      derived from a single choice, not a standing preference.
 *   3. `substitute`— a different brand than either of the above.
 *   4. `null`      — no signal: the offer has no brand, OR the household has no
 *      liked brand and no selected-offer brand to compare against.
 */
export function brandQualifier(
  item: ShoppingListItemWithDetails,
  offer: RetailerOfferWithDetails,
  likedBrands: ReadonlySet<string> = new Set(),
): 'preferred' | 'pick' | 'substitute' | null {
  const offerBrand = norm(offer.brand);
  if (!offerBrand) return null;
  if (likedBrands.has(offerBrand)) return 'preferred';
  const selectedBrand = norm(item.offer_brand);
  // No liked-brand match and no selected-offer brand → no signal to qualify by.
  if (likedBrands.size === 0 && !selectedBrand) return null;
  if (selectedBrand && offerBrand === selectedBrand) return 'pick';
  return 'substitute';
}

/**
 * Flatten every selected store's resolved offers into the rows that match this
 * item, preferred-brand-first (preferred → pick → substitute → unqualified),
 * then by lowest resolved price. `likedBrands` is the household's real liked
 * brand-name set (normalized), threaded through to `brandQualifier`. The
 * preferred product is the silent primary line; the caller labels picks +
 * substitutes and tucks the rest behind "Other brands".
 */
export function matchOffersToItem(
  item: ShoppingListItemWithDetails,
  stores: StoreLocationOffers[],
  likedBrands: ReadonlySet<string> = new Set(),
  itemSystemProductIds: ReadonlySet<string> = new Set(),
  // T12.5.1 (E) — the household's preference signal sets (minus `onList`, which is
  // always true for a Planning offer row). Defaults to neutral so callers that
  // only need the price layer keep working.
  badgeSignals: Omit<BadgeInputs, 'onList'> = EMPTY_BADGE_INPUTS,
): MatchedStoreLocationOffer[] {
  const matched: MatchedStoreLocationOffer[] = [];
  for (const store of stores) {
    for (const offer of store.offers) {
      if (!offerMatchesItem(item, offer, itemSystemProductIds)) continue;
      matched.push({
        storeId: store.storeId,
        storeName: store.storeName,
        offer,
        qualifier: brandQualifier(item, offer, likedBrands),
        // The offer is for an on-list product → `on-list` always present.
        badges: deriveBadges(
          {
            title: offer.title,
            brand: offer.brand ?? null,
            systemProductId: offer.systemproduct_id ?? null,
          },
          { ...badgeSignals, onList: true },
        ),
      });
    }
  }
  const rank = (q: MatchedStoreLocationOffer['qualifier']): number =>
    q === 'preferred' ? 0 : q === 'pick' ? 1 : q === 'substitute' ? 2 : 3;
  return matched.sort((a, b) => {
    const byRank = rank(a.qualifier) - rank(b.qualifier);
    if (byRank !== 0) return byRank;
    const ap = resolvedPrice(a.offer);
    const bp = resolvedPrice(b.offer);
    if (ap == null) return 1;
    if (bp == null) return -1;
    return ap - bp;
  });
}

/**
 * The price we'd show for an offer row: the catalog regular price. The sale
 * price overlay is carried by the matched flyer deal (`has_active_deal` marks
 * its presence); the regular price is the durable, always-present number. NULL
 * when the catalog row genuinely has no price — never invented.
 */
export function resolvedPrice(offer: RetailerOfferWithDetails): number | null {
  return offer.regular_price ?? null;
}

// One per-store deal entry on the item's `store_deals` (the wire shape). Carries
// the deal's identity (`flyerdeal_id` + `title` + `brand`) so the picker can list
// a store's deals by flyer title and mark which one is pinned.
export type StoreDealEntry = NonNullable<
  ShoppingListItemWithDetails['store_deals']
>[number];

/**
 * T27.2.7.9.6 — the FULL set of live Flyer deals at one selected store, read from
 * the item's `store_deals` array by `storelocation_id`. Resolved by SystemProduct
 * + retailer/region (the same resolver pass the headline price uses), so it is
 * DECOUPLED from the sparse catalog offer: a store shows deals here whenever a
 * flyer deal is live at its retailer/region, whether or not a `retaileroffers` row
 * exists there.
 *
 * .9.5 carried one cheapest deal per store; .9.6 carries the whole matched set so
 * the Where to buy picker can browse a store's deals and pin one. The set is
 * returned in the order the wire carries it (the backend already orders it
 * cheapest-active-first per store). Empty array when the item has no live deal at
 * that store. This is SEPARATE from `resolvedPrice()` on purpose: `resolvedPrice()`
 * stays the catalog price feeding the collapsed `$X best` summary + the sort; this
 * feeds the picker rows, where we show the real on-sale price (never a struck
 * `regular_price`).
 */
export function storeDealsForStore(
  item: ShoppingListItemWithDetails,
  storeLocationId: string,
): StoreDealEntry[] {
  return (item.store_deals ?? []).filter(
    (d) => d.storelocation_id === storeLocationId,
  );
}

/**
 * T27.2.7.9.6 — the picker's DEFAULT deal for a store when nothing is pinned: the
 * cheapest ACTIVE deal, falling back to the cheapest UPCOMING only when no active
 * deal exists there (active-over-upcoming, then cheapest — mirrors .9.5's headline
 * pick). NULL when the store has no live deal. A pure pick over the carried set,
 * independent of wire ordering, so the picker's "what shows when unpinned" is
 * stable even if the backend order changes.
 */
export function defaultStoreDeal(
  item: ShoppingListItemWithDetails,
  storeLocationId: string,
): StoreDealEntry | null {
  const deals = storeDealsForStore(item, storeLocationId);
  let best: StoreDealEntry | null = null;
  for (const d of deals) {
    if (!best || isBetterStoreDeal(d, best)) best = d;
  }
  return best;
}

/** active-over-upcoming, then cheapest within a tier (the default-pick order). */
function isBetterStoreDeal(candidate: StoreDealEntry, existing: StoreDealEntry): boolean {
  if (candidate.status !== existing.status) return candidate.status === 'active';
  return candidate.sale_price < existing.sale_price;
}

/**
 * T27.2.7.9.6 — the PINNED deal at a store: the `store_deals` entry whose
 * `flyerdeal_id` equals the item's pinned `flyerdeal_id`. NULL when nothing is
 * pinned, or the pinned deal is not live at this store (the pin may point at a
 * deal at a different store, or one no longer live — the caller falls back to the
 * default). The picker marks this entry as chosen and re-opens it.
 */
export function pinnedStoreDeal(
  item: ShoppingListItemWithDetails,
  storeLocationId: string,
): StoreDealEntry | null {
  const pinnedId = item.flyerdeal_id ?? null;
  if (!pinnedId) return null;
  return (
    storeDealsForStore(item, storeLocationId).find(
      (d) => d.flyerdeal_id === pinnedId,
    ) ?? null
  );
}

/**
 * Derive the collapsed-row `storeLocationOfferSummary` — the additive price
 * signal, at most ONE thing.
 *
 *   - No selected stores at all OR stores still loading → `null` (render
 *     NOTHING). The normal baseline: a clean planning item.
 *   - ≥1 matched offer carries an active flyer deal → a `summaryLabel`
 *     `On sale · N stores`.
 *   - Matched offers exist, none on sale → a `summaryLabel` `$X best` (lowest
 *     resolved price).
 *   - The household HAS selected, resolved stores but none carry the item → a
 *     wordless `availabilityDot` (`{ kind: 'dot' }`). This replaces the old
 *     cut-off "Not at your stores" text — a quiet dot, never clipped at 360px.
 *
 * `hasSelectedStores` distinguishes "no stores connected yet" (baseline, no
 * summary) from "stores connected but item not carried" (the quiet dot).
 */
export function storeLocationOfferSummary(
  matched: MatchedStoreLocationOffer[],
  opts: { hasSelectedStores: boolean; storesResolved: boolean },
): StoreLocationOfferSummary | null {
  if (matched.length > 0) {
    const onSale = matched.filter((m) => m.offer.has_active_deal);
    if (onSale.length > 0) {
      const storeCount = new Set(onSale.map((m) => m.storeId)).size;
      return {
        kind: 'label',
        label:
          storeCount === 1
            ? 'On sale · 1 store'
            : `On sale · ${storeCount} stores`,
      };
    }
    const prices = matched
      .map((m) => resolvedPrice(m.offer))
      .filter((p): p is number => p != null);
    if (prices.length > 0) {
      const best = Math.min(...prices);
      return { kind: 'label', label: `$${best.toFixed(2)} best` };
    }
    // Matched, but no price on any row — still no fabricated number; treat as
    // baseline (no summary) rather than inventing a value.
    return null;
  }
  // No matched offers. Only show the wordless availability dot once the
  // household has selected stores AND those stores' offers have resolved —
  // otherwise it's the clean no-offer baseline (nothing while loading).
  if (opts.hasSelectedStores && opts.storesResolved) {
    return { kind: 'dot' };
  }
  return null;
}

/**
 * Construct the inline ⚙ → Settings deep-link. The receiving surface
 * (HouseholdProductDetailPage) renders "‹ List" from `from=list` and scrolls +
 * highlights the `[data-section]` matching `section`. This task only builds the
 * URL; it never touches the settings surface.
 */
export function settingsUrl(
  productsBase: string,
  productId: string,
  opts?: { section?: 'brands' | 'size' },
): string {
  const params = new URLSearchParams({ from: 'list' });
  if (opts?.section) params.set('section', opts.section);
  return `${productsBase}/${productId}?${params.toString()}`;
}

/**
 * The picked-line copy for the optional where-to-buy selection. When a store is
 * picked we show where + price; cleared, the calm "decide later" default.
 */
export function pickedLine(picked: MatchedStoreLocationOffer | null): {
  text: string;
  active: boolean;
} {
  if (!picked) {
    return {
      text: "Decide later — we'll suggest the best stop.",
      active: false,
    };
  }
  const price = resolvedPrice(picked.offer);
  const where = picked.storeName ?? 'your store';
  return {
    text:
      price != null
        ? `On your ${where} stop · $${price.toFixed(2)}`
        : `On your ${where} stop`,
    active: true,
  };
}
